'use client';

import React from 'react';
import { SessionContext } from '@benzinga/session-context';
import { taboolaAdManager, type TaboolaItemInfo, type TaboolaSlotRenderEvent } from '@benzinga/ads-utils';
import { trackAdImpression } from '../utils/trackAdImpression';

interface UseTaboolaAdImpressionProps {
  containerRef: React.RefObject<HTMLElement | null>;
  placementName: string;
}

export function useTaboolaAdImpression({ containerRef, placementName }: UseTaboolaAdImpressionProps): void {
  const session = React.useContext(SessionContext);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const observedElementsRef = React.useRef<Set<Element>>(new Set());
  const itemsRef = React.useRef<TaboolaItemInfo[]>([]);
  const impressedIdsRef = React.useRef<Set<string>>(new Set());
  const sessionRef = React.useRef(session);
  const placementNameRef = React.useRef(placementName);

  React.useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  React.useEffect(() => {
    placementNameRef.current = placementName;
  }, [placementName]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const itemId = entry.target.getAttribute('data-item-id');
          if (!itemId || impressedIdsRef.current.has(itemId)) return;

          const item = itemsRef.current.find(item => item.itemId === itemId);
          if (item) {
            impressedIdsRef.current.add(item.itemId);
            trackAdImpression({
              destinationUrl: item.destinationUrl,
              partnerId: 'taboola',
              session: sessionRef.current,
              unitType: placementNameRef.current,
            });
          }
        });
      },
      { rootMargin: '0px', threshold: 0.1 },
    );
    observerRef.current = observer;
    const observedElements = observedElementsRef.current;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      observedElements.clear();
    };
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanup = taboolaAdManager.observeContainer(container, (event: TaboolaSlotRenderEvent) => {
      const existingIds = new Set(itemsRef.current.map(itm => itm.itemId));
      const newItems = event.items.filter(itm => !existingIds.has(itm.itemId));

      if (newItems.length > 0) {
        itemsRef.current = [...itemsRef.current, ...newItems];

        newItems.forEach(item => {
          if (!observedElementsRef.current.has(item.element) && observerRef.current) {
            observerRef.current.observe(item.element);
            observedElementsRef.current.add(item.element);
          }
        });
      }
    });

    return cleanup;
  }, [containerRef]);
}
